// @flow

import PropTypes from 'prop-types';
import * as React from 'react';

import impossible from 'common/types/impossible';
import Checkbox from 'common/components/Checkbox';
import FontAwesomeIcon, {
  check,
  plus,
  minus
} from 'common/components/FontAwesomeIcon';

import styles from './CheckboxGroupInput.scss';

type Item = {| name: string, id: number |};

export type Props = {|
  onChange: () => mixed,
  values: Array<Item>,
  defaultVisibleValuesCount?: number,
  selectedValues: Array<number>
|};

type NoToggling = {| state: 'NO_TOGGLING' |};
type PartiallyHiddenButNoSelectedValuesHidden = {|
  state: 'PARTIALLY_HIDDEN_BUT_NO_SELECTED_VALUES_HIDDEN',
  visibleValues: Array<Item>,
  hiddenValuesCount: number
|};
type PartiallyHiddenAndSomeSelectedValuesHidden = {|
  state: 'PARTIALLY_HIDDEN_AND_SOME_SELECTED_VALUES_HIDDEN',
  visibleValues: Array<Item>,
  hiddenValuesCount: number,
  hiddenButSelectedValues: Array<Item>
|};
type ToggledVisible = {|
  state: 'TOGGLED_VISIBLE',
  visibleValues: Array<Item>
|};
type ToggleState =
  | NoToggling
  | PartiallyHiddenButNoSelectedValuesHidden
  | PartiallyHiddenAndSomeSelectedValuesHidden
  | ToggledVisible;

function calculateToggleState({
  defaultVisibleValuesCount,
  values,
  selectedValues,
  initiallySelectedValues,
  askedToShowMore
}): ToggleState {
  if (defaultVisibleValuesCount === undefined) {
    return { state: 'NO_TOGGLING' };
  }
  if (defaultVisibleValuesCount >= values.length) {
    return { state: 'NO_TOGGLING' };
  }
  if (initiallySelectedValues.length >= values.length) {
    return { state: 'NO_TOGGLING' };
  }

  // Always start with the initially selected values as the first visible values
  // and then show the rest.
  const sortedValues = values
    .filter(item => initiallySelectedValues.includes(item.id))
    .concat(values.filter(item => !initiallySelectedValues.includes(item.id)));

  if (askedToShowMore) {
    return { state: 'TOGGLED_VISIBLE', visibleValues: sortedValues };
  }

  const visibleValuesCount = Math.max(
    defaultVisibleValuesCount,
    initiallySelectedValues.length
  );

  const visibleValues = sortedValues.slice(0, visibleValuesCount);
  const hiddenValues = sortedValues.slice(visibleValuesCount);

  const hiddenButSelectedValues = hiddenValues.filter(value =>
    selectedValues.includes(value.id)
  );

  if (hiddenButSelectedValues.length > 0) {
    return {
      state: 'PARTIALLY_HIDDEN_AND_SOME_SELECTED_VALUES_HIDDEN',
      visibleValues,
      hiddenValuesCount: hiddenValues.length,
      hiddenButSelectedValues
    };
  }

  return {
    state: 'PARTIALLY_HIDDEN_BUT_NO_SELECTED_VALUES_HIDDEN',
    visibleValues,
    hiddenValuesCount: hiddenValues.length
  };
}

function CheckboxGroupInput(props: Props): React.Node {
  const [askedToShowMore, setAskedToShowMore] = React.useState(false);
  const [initiallySelectedValues] = React.useState(props.selectedValues);

  const toggleState: ToggleState = calculateToggleState({
    defaultVisibleValuesCount: props.defaultVisibleValuesCount,
    values: props.values,
    selectedValues: props.selectedValues,
    initiallySelectedValues,
    askedToShowMore
  });

  switch (toggleState.state) {
    case 'NO_TOGGLING':
      return (
        <div className={styles.gridContainer}>
          <CheckboxList
            values={props.values}
            selectedValues={props.selectedValues}
            onChange={props.onChange}
          />
        </div>
      );
    case 'PARTIALLY_HIDDEN_BUT_NO_SELECTED_VALUES_HIDDEN':
      return (
        <div className={styles.gridContainer}>
          <CheckboxList
            values={toggleState.visibleValues}
            selectedValues={props.selectedValues}
            onChange={props.onChange}
          />
          <ShowMoreValues
            valuesCount={toggleState.hiddenValuesCount}
            onShowMore={() => setAskedToShowMore(true)}
          />
        </div>
      );
    case 'PARTIALLY_HIDDEN_AND_SOME_SELECTED_VALUES_HIDDEN':
      return (
        <>
          <div className={styles.gridContainer}>
            <CheckboxList
              values={toggleState.visibleValues}
              selectedValues={props.selectedValues}
              onChange={props.onChange}
            />
            <ShowMoreValues
              valuesCount={toggleState.hiddenValuesCount}
              onShowMore={() => setAskedToShowMore(true)}
            />
          </div>
          <HiddenButSelectedValuesList
            hiddenButSelectedValues={toggleState.hiddenButSelectedValues}
          />
        </>
      );
    case 'TOGGLED_VISIBLE':
      return (
        <div className={styles.gridContainer}>
          <CheckboxList
            values={toggleState.visibleValues}
            selectedValues={props.selectedValues}
            onChange={props.onChange}
          />
          <ShowLessValues onShowLess={() => setAskedToShowMore(false)} />
        </div>
      );
    default:
      return impossible(toggleState.state);
  }
}

CheckboxGroupInput.defaultProps = ({
  selectedValues: []
}: {| selectedValues: Array<number> |});

CheckboxGroupInput.propTypes = ({
  onChange: PropTypes.func.isRequired,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.number
    })
  ).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.number).isRequired
}: $ObjMap<Props, any>);

export default CheckboxGroupInput;

function CheckboxList({ values, selectedValues, onChange }) {
  return values.map(value => (
    <Checkbox
      key={value.id}
      name={value.name}
      inputProps={{
        value: value.id,
        checked: selectedValues.indexOf(value.id) >= 0,
        onChange: onChange
      }}
    />
  ));
}

function ShowMoreValues({ valuesCount, onShowMore }) {
  return (
    <button
      type="button"
      className={styles.toggleOptionsButton}
      onClick={() => {
        onShowMore();
      }}
    >
      <FontAwesomeIcon icon={plus} className={styles.toggleOptionsButtonIcon} />
      <span className={styles.toggleOptionsButtonText}>
        {__('shared.show_all_toggle.show_more')} ({valuesCount})
      </span>
    </button>
  );
}

function ShowLessValues({ onShowLess }) {
  return (
    <button
      type="button"
      className={styles.toggleOptionsButton}
      onClick={() => {
        onShowLess();
      }}
    >
      <FontAwesomeIcon
        icon={minus}
        className={styles.toggleOptionsButtonIcon}
      />
      <span className={styles.toggleOptionsButtonText}>
        {__('shared.show_all_toggle.hide')}
      </span>
    </button>
  );
}

function HiddenButSelectedValuesList({ hiddenButSelectedValues }) {
  return (
    <div
      data-testid="hidden-but-selected-values"
      className={styles.hiddenButSelectedOptionsContainer}
    >
      {hiddenButSelectedValues.map(val => (
        <span key={val.id} className={styles.hiddenButSelectedOption}>
          <FontAwesomeIcon
            icon={check}
            className={styles.hiddenButSelectedOptionIcon}
          />
          {val.name}
          <input value={val.id} type="hidden" />
        </span>
      ))}
    </div>
  );
}

export function getSelectedValues(wrapper: HTMLElement): Array<number> {
  const checkedCheckboxes = [].slice
    .call(wrapper.querySelectorAll('input[type="checkbox"]'))
    .filter(node => node.checked);
  const hiddenInputs = [].slice.call(
    wrapper.querySelectorAll('input[type="hidden"]')
  );

  const values = [...checkedCheckboxes, ...hiddenInputs].map(node =>
    parseInt(node.value, 10)
  );

  return values;
}
